import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail, FileText, Send, Eye, ChevronRight,
  X, Edit3, Code, Palette, Smartphone, Monitor,
  ArrowLeft, Save, ToggleLeft, ToggleRight,
  Type, Paintbrush, Layout, History, TestTube,
  AlertCircle, Check, Image as ImageIcon,
  Bold, Italic, Underline, List, AlignLeft, AlignCenter,
  Link2, Heading2, Heading3, Minus,
  MailX, Users, Tag
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { AdminEmailService } from '../services/adminEmailService';
import { CrmContactsSection, CrmListsSection, CrmCampaignsSection } from './admin/email/CrmSections';

// ── Types ────────────────────────────────────────────────────────

interface EmailTemplate {
  id: string;
  name: string;
  slug: string;
  description: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  text_color: string;
  background_color: string;
  footer_background: string;
  heading_font: string;
  body_font: string;
  layout_html: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface MergeField {
  key: string;
  label: string;
  description: string;
  example: string;
}

interface SystemEmail {
  id: string;
  email_key: string;
  name: string;
  description: string;
  category: string;
  subject: string;
  body_html: string;
  body_json: any;
  template_id: string | null;
  template_name: string | null;
  template_slug: string | null;
  merge_fields: MergeField[];
  is_published: boolean;
  from_service: string;
  trigger_description: string;
  stats?: { total?: number; success_count?: number; last_sent?: string };
  created_at: string;
  updated_at: string;
}

interface EmailVersion {
  id: string;
  version_number: number;
  subject: string;
  changed_by: string;
  change_note: string;
  created_at: string;
}

type SubSection = 'overview' | 'contacts' | 'lists' | 'campaigns' | 'templates' | 'system-emails' | 'unsubscribe';
type EditorMode = 'visual' | 'html';
type PreviewDevice = 'desktop' | 'mobile';

// ── Design tokens ───────────────────────────────────────────────

const COLORS = {
  primary: '#1F6C4F',
  primaryDark: '#113C2B',
  primaryLight: '#5FA287',
  subtle: '#E8F2ED',
  limestone: '#F3F3F3',
  surface: '#FFFFFF',
  ink: '#0B1614',
  text: '#374151',
  textSecondary: '#6B7280',
  textTertiary: '#9CA3AF',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  success: '#065F46',
  successBg: '#D1FAE5',
  warning: '#92400E',
  warningBg: '#FEF3C7',
  error: '#DC2626',
  errorBg: '#FEE2E2',
  info: '#1E40AF',
  infoBg: '#DBEAFE',
  purple: '#5B21B6',
  purpleBg: '#EDE9FE',
};

const RADIUS = { sm: 6, md: 10, lg: 14, xl: 20, pill: 9999 };
const SHADOW = {
  sm: '0px 1px 2px rgba(0,0,0,0.05)',
  md: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)',
  lg: '0px 4px 12px rgba(0,0,0,0.15)',
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  transactional: { bg: COLORS.infoBg, text: COLORS.info, label: 'Transactional' },
  admin: { bg: COLORS.warningBg, text: COLORS.warning, label: 'Admin' },
  support: { bg: COLORS.purpleBg, text: COLORS.purple, label: 'Support' },
};

// ── Shared UI components ────────────────────────────────────────

const Spinner: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      border: `3px solid ${COLORS.limestone}`,
      borderTopColor: COLORS.primary,
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const Toast: React.FC<{
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const t = setTimeout(() => onCloseRef.current(), 4000);
    return () => clearTimeout(t);
  }, []);

  const colors = {
    success: { bg: COLORS.successBg, text: COLORS.success, icon: Check },
    error: { bg: COLORS.errorBg, text: COLORS.error, icon: AlertCircle },
    info: { bg: COLORS.infoBg, text: COLORS.info, icon: Mail },
  }[type];
  const Icon = colors.icon;

  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 10000,
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 20px', borderRadius: RADIUS.md,
      background: colors.bg, color: colors.text,
      boxShadow: SHADOW.lg, fontSize: 14, fontWeight: 600,
      animation: 'slideIn 0.3s ease-out',
    }}>
      <Icon size={16} />
      {message}
      <button onClick={onClose} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: colors.text, marginLeft: 8, padding: 0,
      }}>
        <X size={14} />
      </button>
      <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
};

const Badge: React.FC<{ bg: string; text: string; label: string }> = ({ bg, text, label }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: RADIUS.pill,
    fontSize: 11, fontWeight: 600, background: bg, color: text,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  }}>
    {label}
  </span>
);

const ConfirmDialog: React.FC<{
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
}> = ({ title, message, confirmLabel, onConfirm, onCancel, destructive }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(17,60,43,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  }}>
    <div style={{
      background: COLORS.surface, borderRadius: RADIUS.xl, padding: 32,
      width: 420, maxWidth: '90vw', boxShadow: SHADOW.lg,
    }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: COLORS.ink }}>{title}</h3>
      <p style={{ color: COLORS.textSecondary, fontSize: 14, margin: '0 0 24px', lineHeight: 1.5 }}>{message}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onCancel} style={{
          padding: '10px 20px', borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`,
          background: COLORS.surface, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.text,
        }}>Cancel</button>
        <button onClick={onConfirm} style={{
          padding: '10px 20px', borderRadius: RADIUS.md, border: 'none',
          background: destructive ? COLORS.error : COLORS.primary,
          color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
        }}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

// ── Main component ───────────────────────────────────────────────

export const AdminEmailManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = (searchParams.get('section') as SubSection) || 'overview';
  const editId = searchParams.get('edit');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const setSection = (s: SubSection) => setSearchParams({ section: s });
  const showToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info' = 'success') => setToast({ message, type }),
    []
  );
  const dismissToast = useCallback(() => setToast(null), []);

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={dismissToast} />}

      {editId ? (
        <EmailEditor
          emailId={editId}
          onBack={() => setSearchParams({ section: 'system-emails' })}
          onToast={showToast}
        />
      ) : (
        <>
          {/* Sub-navigation */}
          <div style={{
            display: 'flex', gap: 2, marginBottom: 24,
            background: COLORS.limestone, borderRadius: RADIUS.lg, padding: 4,
          }}>
            {([
              { key: 'overview' as SubSection, label: 'Overview', icon: Mail },
              { key: 'contacts' as SubSection, label: 'Contacts', icon: Users },
              { key: 'lists' as SubSection, label: 'Lists', icon: Tag },
              { key: 'campaigns' as SubSection, label: 'Campaigns', icon: Send },
              { key: 'templates' as SubSection, label: 'Templates', icon: Layout },
              { key: 'system-emails' as SubSection, label: 'System Emails', icon: FileText },
              { key: 'unsubscribe' as SubSection, label: 'Unsubscribe', icon: MailX },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setSection(tab.key)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, padding: '10px 16px', borderRadius: RADIUS.md, border: 'none',
                  cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  transition: 'all 0.2s ease-in-out',
                  background: section === tab.key ? COLORS.primary : 'transparent',
                  color: section === tab.key ? '#FFF' : COLORS.textSecondary,
                  boxShadow: section === tab.key ? SHADOW.sm : 'none',
                }}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {section === 'overview' && <OverviewSection onNavigate={setSection} />}
          {section === 'contacts' && <CrmContactsSection onToast={showToast} />}
          {section === 'lists' && <CrmListsSection onToast={showToast} />}
          {section === 'campaigns' && <CrmCampaignsSection onToast={showToast} />}
          {section === 'templates' && <TemplatesSection onToast={showToast} />}
          {section === 'system-emails' && (
            <SystemEmailsSection
              onEdit={(id) => setSearchParams({ section: 'system-emails', edit: id })}
              onToast={showToast}
            />
          )}
          {section === 'unsubscribe' && <UnsubscribeSection onToast={showToast} />}
        </>
      )}
    </div>
  );
};

// ── Overview Section ────────────────────────────────────────────

const OverviewSection: React.FC<{ onNavigate: (s: SubSection) => void }> = ({ onNavigate }) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [recentEmails, setRecentEmails] = useState<SystemEmail[]>([]);

  useEffect(() => {
    Promise.all([
      AdminEmailService.getStats(),
      AdminEmailService.listSystemEmails(),
    ]).then(([statsData, emailData]) => {
      setStats(statsData.stats);
      const sorted = (emailData.emails || [])
        .filter((e: SystemEmail) => e.stats?.last_sent)
        .sort((a: SystemEmail, b: SystemEmail) =>
          new Date(b.stats?.last_sent || 0).getTime() - new Date(a.stats?.last_sent || 0).getTime()
        )
        .slice(0, 5);
      setRecentEmails(sorted);
    }).catch(e => { console.error('Overview load failed:', e); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <AlertCircle size={24} color={COLORS.error} style={{ marginBottom: 8 }} />
      <div style={{ color: COLORS.error, fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Failed to load email stats</div>
      <div style={{ color: COLORS.textSecondary, fontSize: 13 }}>Check your connection and try refreshing.</div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: COLORS.ink, margin: 0, fontFamily: "'League Spartan', sans-serif" }}>
          Email Management
        </h2>
        <p style={{ color: COLORS.textSecondary, marginTop: 4, fontSize: 14 }}>
          Manage email templates, edit system emails, and control delivery.
        </p>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'System Emails', value: stats?.total_emails ?? 0, icon: Mail, color: COLORS.primary },
          { label: 'Published', value: stats?.published ?? 0, icon: Check, color: COLORS.success },
          { label: 'Unpublished', value: stats?.unpublished ?? 0, icon: AlertCircle, color: COLORS.error },
          { label: 'Sends (7d)', value: stats?.sends_last_7_days ?? 0, icon: Send, color: COLORS.info },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} style={{
              background: COLORS.surface, borderRadius: RADIUS.lg, padding: '20px 24px',
              boxShadow: SHADOW.md,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: RADIUS.md,
                  background: `${stat.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={18} color={stat.color} />
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.ink }}>{stat.value}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.textSecondary, marginTop: 2 }}>{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Quick access + recent activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Quick access cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.ink, margin: '0 0 4px' }}>Quick Access</h3>
          {[
            { section: 'templates' as SubSection, icon: Palette, label: 'Templates', desc: 'Brand colors, fonts, and email layouts', color: COLORS.primary },
            { section: 'system-emails' as SubSection, icon: FileText, label: 'System Emails', desc: 'Edit, preview, and toggle all transactional emails', color: COLORS.info },
            { section: 'unsubscribe' as SubSection, icon: MailX, label: 'Unsubscribe', desc: 'Preference categories, landing-page content, and opt-out analytics', color: COLORS.purple },
          ].map(card => {
            const Icon = card.icon;
            return (
              <button
                key={card.section}
                onClick={() => onNavigate(card.section)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: 20,
                  background: COLORS.surface, borderRadius: RADIUS.lg,
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s ease-in-out',
                  boxShadow: SHADOW.md,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = SHADOW.lg; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = SHADOW.sm; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: RADIUS.md,
                  background: `${card.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={22} color={card.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: COLORS.ink }}>{card.label}</div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>{card.desc}</div>
                </div>
                <ChevronRight size={18} color={COLORS.textTertiary} />
              </button>
            );
          })}
        </div>

        {/* Recent activity */}
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.ink, margin: '0 0 16px' }}>Recent Activity</h3>
          <div style={{
            background: COLORS.surface, borderRadius: RADIUS.lg,
            overflow: 'hidden', boxShadow: SHADOW.md,
          }}>
            {recentEmails.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: COLORS.textTertiary, fontSize: 14 }}>
                No emails sent yet
              </div>
            ) : (
              recentEmails.map((email, i) => (
                <div key={email.id} style={{
                  padding: '12px 16px', borderBottom: i < recentEmails.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: email.is_published ? COLORS.success : COLORS.error,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.ink }}>{email.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                      {email.stats?.total || 0} sent
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textTertiary }}>
                    {email.stats?.last_sent ? new Date(email.stats.last_sent).toLocaleDateString() : '—'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Templates Section ───────────────────────────────────────────

const TemplatesSection: React.FC<{ onToast: (msg: string, type: 'success' | 'error' | 'info') => void }> = ({ onToast }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<Partial<EmailTemplate>>({});

  const load = useCallback(() => {
    setLoading(true);
    AdminEmailService.listTemplates()
      .then(d => setTemplates(d.templates))
      .catch(e => { console.error(e); onToast('Failed to load templates', 'error'); })
      .finally(() => setLoading(false));
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (t: EmailTemplate) => {
    setEditing(t.id);
    setEditForm({ ...t });
  };

  const saveTemplate = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      // Only send editable fields — exclude id, slug, created_at, updated_at
      const { id, slug, created_at, updated_at, ...editable } = editForm as any;
      await AdminEmailService.updateTemplate(editing, editable);
      onToast('Template saved', 'success');
      setEditing(null);
      load();
    } catch (e: any) {
      onToast(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const colorFields: { key: keyof EmailTemplate; label: string }[] = [
    { key: 'primary_color', label: 'Primary' },
    { key: 'secondary_color', label: 'Secondary' },
    { key: 'accent_color', label: 'Accent' },
    { key: 'text_color', label: 'Text' },
    { key: 'background_color', label: 'Background' },
    { key: 'footer_background', label: 'Footer' },
  ];

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.ink, margin: 0, fontFamily: "'League Spartan', sans-serif" }}>Email Templates</h2>
        <p style={{ color: COLORS.textSecondary, marginTop: 4, fontSize: 14 }}>
          Define the visual wrapper for your emails — brand colors, typography, and layout.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {templates.map(t => (
          <div key={t.id} style={{
            background: COLORS.surface, borderRadius: RADIUS.lg,
            boxShadow: SHADOW.md,
            overflow: 'hidden', transition: 'all 0.2s ease-in-out',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: editing === t.id ? `1px solid ${COLORS.border}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ display: 'flex', gap: 3 }}>
                  {[t.primary_color, t.secondary_color, t.accent_color].map((c, i) => (
                    <div key={i} style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: c, border: `2px solid ${COLORS.surface}`,
                      boxShadow: SHADOW.sm,
                    }} />
                  ))}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: COLORS.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {t.name}
                    {t.is_default && <Badge bg={COLORS.subtle} text={COLORS.primary} label="Default" />}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>{t.description}</div>
                </div>
              </div>
              <button
                onClick={() => editing === t.id ? setEditing(null) : startEdit(t)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                  borderRadius: RADIUS.pill, border: `1.5px solid ${editing === t.id ? COLORS.primary : COLORS.border}`,
                  background: editing === t.id ? COLORS.subtle : COLORS.surface,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  color: editing === t.id ? COLORS.primary : COLORS.text,
                  transition: 'all 0.15s ease-in-out',
                }}
              >
                {editing === t.id ? <X size={14} /> : <Edit3 size={14} />}
                {editing === t.id ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {/* Edit panel */}
            {editing === t.id && (
              <div style={{ padding: 24 }}>
                {/* Colors */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{
                    display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textSecondary,
                    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10,
                  }}>
                    <Paintbrush size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Brand Colors
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {colorFields.map(cf => (
                      <div key={cf.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="color"
                          value={(editForm as any)[cf.key] || '#000000'}
                          onChange={e => setEditForm(f => ({ ...f, [cf.key]: e.target.value }))}
                          style={{
                            width: 40, height: 40, border: `1.5px solid ${COLORS.border}`,
                            borderRadius: RADIUS.sm, cursor: 'pointer', padding: 2,
                          }}
                        />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{cf.label}</div>
                          <div style={{ fontSize: 11, color: COLORS.textTertiary, fontFamily: 'monospace' }}>
                            {(editForm as any)[cf.key]}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fonts */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{
                    display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textSecondary,
                    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10,
                  }}>
                    <Type size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Typography
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {(['heading_font', 'body_font'] as const).map(fk => (
                      <div key={fk}>
                        <label style={{ fontSize: 12, fontWeight: 500, color: COLORS.textSecondary, display: 'block', marginBottom: 6 }}>
                          {fk === 'heading_font' ? 'Heading Font' : 'Body Font'}
                        </label>
                        <select
                          value={(editForm as any)[fk] || ''}
                          onChange={e => setEditForm(f => ({ ...f, [fk]: e.target.value }))}
                          style={{
                            width: '100%', padding: '8px 12px', borderRadius: RADIUS.sm,
                            border: `1.5px solid ${COLORS.border}`, fontSize: 13, color: COLORS.text,
                            background: COLORS.surface, fontFamily: 'inherit',
                          }}
                        >
                          <option value="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">System Default (Inter)</option>
                          <option value="'Georgia', serif">Georgia (Serif)</option>
                          <option value="'Helvetica Neue', Arial, sans-serif">Helvetica Neue</option>
                          <option value="'Merriweather', serif">Merriweather</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Layout HTML */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{
                    display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textSecondary,
                    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10,
                  }}>
                    <Code size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Layout HTML
                  </label>
                  <textarea
                    value={editForm.layout_html || ''}
                    onChange={e => setEditForm(f => ({ ...f, layout_html: e.target.value }))}
                    style={{
                      width: '100%', height: 200, padding: 12, borderRadius: RADIUS.sm,
                      border: `1.5px solid ${COLORS.border}`, fontFamily: 'monospace', fontSize: 12,
                      lineHeight: 1.5, resize: 'vertical', color: COLORS.text, boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ fontSize: 11, color: COLORS.textTertiary, marginTop: 6 }}>
                    Use {'{{CONTENT}}'} where email body inserts. Colors: {'{{PRIMARY_COLOR}}'}, {'{{SECONDARY_COLOR}}'}, {'{{TEXT_COLOR}}'}, etc.
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setEditing(null)} style={{
                    padding: '10px 20px', borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`,
                    background: COLORS.surface, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.text,
                  }}>Cancel</button>
                  <button onClick={saveTemplate} disabled={saving} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '10px 24px', borderRadius: RADIUS.md, border: 'none',
                    background: COLORS.primary, color: '#fff', cursor: 'pointer',
                    fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1,
                    transition: 'all 0.15s ease-in-out',
                  }}>
                    <Save size={14} />
                    {saving ? 'Saving...' : 'Save Template'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── System Emails Section ───────────────────────────────────────

const SystemEmailsSection: React.FC<{
  onEdit: (id: string) => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}> = ({ onEdit, onToast }) => {
  const [emails, setEmails] = useState<SystemEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [toggling, setToggling] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ email: SystemEmail } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    AdminEmailService.listSystemEmails()
      .then(d => setEmails(d.emails))
      .catch(e => { console.error(e); onToast('Failed to load emails', 'error'); })
      .finally(() => setLoading(false));
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (email: SystemEmail) => {
    // If publishing, do it immediately. If unpublishing, confirm first.
    if (email.is_published) {
      setConfirmAction({ email });
      return;
    }
    await doToggle(email);
  };

  const doToggle = async (email: SystemEmail) => {
    setToggling(email.id);
    setConfirmAction(null);
    try {
      if (email.is_published) {
        await AdminEmailService.unpublishEmail(email.id);
        onToast(`${email.name} taken offline`, 'info');
      } else {
        await AdminEmailService.publishEmail(email.id);
        onToast(`${email.name} published`, 'success');
      }
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_published: !e.is_published } : e));
    } catch (e: any) {
      onToast(e.message || 'Toggle failed', 'error');
    } finally {
      setToggling(null);
    }
  };

  const categories = ['all', 'transactional', 'admin', 'support'];
  const filtered = filter === 'all' ? emails : emails.filter(e => e.category === filter);

  if (loading) return <Spinner />;

  return (
    <div>
      {confirmAction && (
        <ConfirmDialog
          title="Take Email Offline?"
          message={`"${confirmAction.email.name}" will stop sending to all users. Any workflow that triggers this email will silently skip it.`}
          confirmLabel="Take Offline"
          destructive
          onConfirm={() => doToggle(confirmAction.email)}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.ink, margin: 0, fontFamily: "'League Spartan', sans-serif" }}>System Emails</h2>
          <p style={{ color: COLORS.textSecondary, marginTop: 4, fontSize: 14 }}>
            Every email sent by SendGrid. Edit content, toggle on/off, or preview.
          </p>
        </div>
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {categories.map(cat => {
          const count = cat === 'all' ? emails.length : emails.filter(e => e.category === cat).length;
          const isActive = filter === cat;
          return (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              style={{
                padding: '6px 16px', borderRadius: RADIUS.pill, border: '1.5px solid',
                borderColor: isActive ? COLORS.primary : COLORS.border,
                background: isActive ? COLORS.primary : COLORS.surface,
                color: isActive ? '#fff' : COLORS.textSecondary,
                cursor: 'pointer', fontSize: 13, fontWeight: 500,
                textTransform: 'capitalize', transition: 'all 0.15s ease-in-out',
              }}
            >
              {cat === 'all' ? 'All' : cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Email table */}
      <div style={{
        background: COLORS.surface, borderRadius: RADIUS.lg,
        overflow: 'hidden', boxShadow: SHADOW.md,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Status', 'Email', 'Category', 'Subject', 'Sends', 'Last Sent', 'Actions'].map(h => (
                <th key={h} style={{
                  padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600,
                  color: COLORS.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em',
                  background: '#F3F3F3', borderBottom: `1px solid ${COLORS.border}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(email => {
              const cat = CATEGORY_COLORS[email.category] || { bg: COLORS.limestone, text: COLORS.textSecondary, label: email.category };
              return (
                <tr
                  key={email.id}
                  style={{
                    opacity: email.is_published ? 1 : 0.55,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                >
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.borderLight}` }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: email.is_published ? '#059669' : COLORS.error,
                    }} />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.borderLight}` }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.ink }}>{email.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>
                      {email.trigger_description
                        ? email.trigger_description.length > 60
                          ? email.trigger_description.substring(0, 60) + '...'
                          : email.trigger_description
                        : '—'}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.borderLight}` }}>
                    <Badge bg={cat.bg} text={cat.text} label={cat.label} />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.borderLight}`, maxWidth: 200 }}>
                    <div style={{
                      fontSize: 13, color: COLORS.text, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {email.subject}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.borderLight}` }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.ink }}>
                      {email.stats?.total ?? 0}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.borderLight}` }}>
                    <span style={{ fontSize: 12, color: COLORS.textTertiary }}>
                      {email.stats?.last_sent ? new Date(email.stats.last_sent).toLocaleDateString() : '—'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.borderLight}` }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => togglePublish(email)}
                        disabled={toggling === email.id}
                        title={email.is_published ? 'Take offline' : 'Publish'}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 34, height: 34, borderRadius: RADIUS.sm,
                          border: `1.5px solid ${COLORS.border}`, background: COLORS.surface,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        {email.is_published ?
                          <ToggleRight size={16} color="#059669" /> :
                          <ToggleLeft size={16} color={COLORS.error} />
                        }
                      </button>
                      <button
                        onClick={() => onEdit(email.id)}
                        title="Edit email"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 34, height: 34, borderRadius: RADIUS.sm,
                          border: `1.5px solid ${COLORS.border}`, background: COLORS.surface,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        <Edit3 size={16} color={COLORS.text} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Email Editor ────────────────────────────────────────────────

const EmailEditor: React.FC<{
  emailId: string;
  onBack: () => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}> = ({ emailId, onBack, onToast }) => {
  const [email, setEmail] = useState<SystemEmail | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [versions, setVersions] = useState<EmailVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<EditorMode>('visual');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [showVersions, setShowVersions] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    // Reset temporary UI state when switching emails
    setChangeNote('');
    setTestEmail('');
    setShowTestModal(false);
    setShowVersions(false);
    setLoading(true);

    Promise.all([
      AdminEmailService.getSystemEmail(emailId),
      AdminEmailService.listTemplates(),
    ]).then(([emailData, templateData]) => {
      const e = emailData.email;
      setEmail(e);
      setSubject(e.subject);
      setBodyHtml(e.body_html);
      setSelectedTemplateId(e.template_id);
      setVersions(emailData.versions || []);
      setTemplates(templateData.templates);
    }).catch(e => { console.error(e); onToast('Failed to load email', 'error'); })
      .finally(() => setLoading(false));
  }, [emailId, onToast]);

  // Preview debounce
  useEffect(() => {
    if (!bodyHtml || !email) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const mergeData: Record<string, string> = {};
        email.merge_fields?.forEach(f => { mergeData[f.key] = f.example; });
        const res = await AdminEmailService.previewEmail({
          body_html: bodyHtml, template_id: selectedTemplateId, merge_data: mergeData,
        });
        if (!cancelled) setPreviewHtml(res.html);
      } catch (err) {
        console.warn('Preview render failed:', err);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bodyHtml, selectedTemplateId, email]);

  // Write to iframe
  useEffect(() => {
    if (previewIframeRef.current && previewHtml) {
      const doc = previewIframeRef.current.contentDocument;
      if (doc) { doc.open(); doc.write(previewHtml); doc.close(); }
    }
  }, [previewHtml]);

  const save = async () => {
    setSaving(true);
    try {
      await AdminEmailService.updateSystemEmail(emailId, {
        subject, body_html: bodyHtml, template_id: selectedTemplateId,
        change_note: changeNote || undefined,
      });
      setChangeNote('');
      // Refresh full email state from server to stay in sync
      const res = await AdminEmailService.getSystemEmail(emailId);
      const e = res.email;
      setEmail(e);
      setSubject(e.subject);
      setBodyHtml(e.body_html);
      setSelectedTemplateId(e.template_id);
      setVersions(res.versions || []);
      onToast('Email saved', 'success');
    } catch (e: any) {
      onToast(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    setTestSending(true);
    try {
      const mergeData: Record<string, string> = {};
      email?.merge_fields?.forEach(f => { mergeData[f.key] = f.example; });
      const res = await AdminEmailService.sendTestEmail({
        email_id: emailId, to_email: testEmail, merge_data: mergeData,
      });
      if (res.success) {
        onToast('Test email sent!', 'success');
        setShowTestModal(false);
      } else {
        onToast(`Send failed: ${res.message}`, 'error');
      }
    } catch (e: any) {
      onToast(e.message || 'Send failed', 'error');
    } finally {
      setTestSending(false);
    }
  };

  const execCmd = (cmd: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    if (editorRef.current) setBodyHtml(DOMPurify.sanitize(editorRef.current.innerHTML));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset input so same file can be re-selected
    setUploadingImage(true);
    try {
      const res = await AdminEmailService.uploadImage(file);
      if (res.success && res.url) {
        const safeName = file.name.replace(/[&<>"']/g, c =>
          ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c)
        );
        const imgTag = `<img src="${res.url}" alt="${safeName}" style="max-width:100%;height:auto;" />`;
        if (mode === 'visual') {
          editorRef.current?.focus();
          document.execCommand('insertHTML', false, imgTag);
          if (editorRef.current) setBodyHtml(DOMPurify.sanitize(editorRef.current.innerHTML));
        } else {
          setBodyHtml(prev => prev + imgTag);
        }
        onToast('Image uploaded', 'success');
      }
    } catch (err: any) {
      onToast(err.message || 'Image upload failed', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) return <Spinner />;
  if (!email) return <div style={{ textAlign: 'center', padding: 60, color: COLORS.error }}>Email not found</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, borderRadius: RADIUS.md,
            border: `1.5px solid ${COLORS.border}`, background: COLORS.surface,
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
            <ArrowLeft size={16} color={COLORS.text} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.ink, margin: 0 }}>{email.name}</h2>
              <Badge
                bg={email.is_published ? COLORS.successBg : COLORS.errorBg}
                text={email.is_published ? COLORS.success : COLORS.error}
                label={email.is_published ? 'Published' : 'Offline'}
              />
            </div>
            <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>{email.description}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowVersions(!showVersions)} style={headerBtnStyle}>
            <History size={14} /> History ({versions.length})
          </button>
          <button onClick={() => setShowTestModal(true)} style={headerBtnStyle}>
            <TestTube size={14} /> Send Test
          </button>
          <button onClick={save} disabled={saving} style={{
            ...headerBtnStyle, background: COLORS.primary, color: '#fff',
            border: 'none', opacity: saving ? 0.7 : 1,
          }}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Version history */}
      {showVersions && versions.length > 0 && (
        <div style={{
          background: COLORS.surface, borderRadius: RADIUS.lg,
          padding: '16px 20px', marginBottom: 16, boxShadow: SHADOW.md,
        }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: COLORS.text }}>Version History</h4>
          {versions.map(v => (
            <div key={v.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '8px 12px', borderRadius: RADIUS.sm, background: '#F9FAFB',
              fontSize: 13, marginBottom: 4,
            }}>
              <span style={{ fontWeight: 700, color: COLORS.primary, fontFamily: 'monospace', fontSize: 12 }}>v{v.version_number}</span>
              <span style={{ color: COLORS.text, flex: 1 }}>{v.subject}</span>
              <span style={{ color: COLORS.textTertiary, fontSize: 12 }}>{v.change_note || '—'}</span>
              <span style={{ color: COLORS.textTertiary, fontSize: 12 }}>{v.changed_by}</span>
              <span style={{ color: COLORS.textTertiary, fontSize: 12 }}>{new Date(v.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Subject + Template */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={formLabelStyle}>Subject Line</label>
          <input
            value={subject} onChange={e => setSubject(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: RADIUS.sm,
              border: `1.5px solid ${COLORS.border}`, fontSize: 14, color: COLORS.ink,
              boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
        </div>
        <div>
          <label style={formLabelStyle}>Template</label>
          <select
            value={selectedTemplateId || ''}
            onChange={e => setSelectedTemplateId(e.target.value || null)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: RADIUS.sm,
              border: `1.5px solid ${COLORS.border}`, fontSize: 14, color: COLORS.text,
              background: COLORS.surface, fontFamily: 'inherit',
            }}
          >
            <option value="">No template</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (Default)' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Merge fields */}
      {email.merge_fields.length > 0 && (
        <div style={{
          background: '#F9FAFB', borderRadius: RADIUS.sm, padding: '10px 14px',
          marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary }}>Merge fields:</span>
          {email.merge_fields.map(f => (
            <button
              key={f.key}
              onClick={() => {
                if (mode === 'html') setBodyHtml(prev => prev + `{{${f.key}}}`);
                else execCmd('insertText', `{{${f.key}}}`);
              }}
              title={`${f.description} (e.g. ${f.example})`}
              style={{
                padding: '3px 10px', borderRadius: RADIUS.sm,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                cursor: 'pointer', fontSize: 12, fontFamily: 'monospace',
                color: COLORS.primary, fontWeight: 600, transition: 'all 0.1s',
              }}
            >
              {`{{${f.key}}}`}
            </button>
          ))}
        </div>
      )}

      {/* Mode + device toggles */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 2, background: COLORS.limestone, borderRadius: RADIUS.sm, padding: 3 }}>
          {[
            { m: 'visual' as EditorMode, icon: Edit3, label: 'Visual' },
            { m: 'html' as EditorMode, icon: Code, label: 'HTML' },
          ].map(({ m, icon: I, label }) => (
            <button key={m} onClick={() => {
              // Sync contentEditable HTML into state when switching away from visual
              if (mode === 'visual' && m === 'html' && editorRef.current) {
                setBodyHtml(DOMPurify.sanitize(editorRef.current.innerHTML));
              }
              setMode(m);
            }} style={{
              padding: '6px 14px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
              background: mode === m ? COLORS.surface : 'transparent',
              color: mode === m ? COLORS.primary : COLORS.textSecondary,
              boxShadow: mode === m ? SHADOW.sm : 'none', transition: 'all 0.15s',
            }}>
              <I size={12} /> {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Preview device toggle */}
          {showPreview && (
            <div style={{ display: 'flex', gap: 2, background: COLORS.limestone, borderRadius: RADIUS.sm, padding: 3 }}>
              {[
                { d: 'desktop' as PreviewDevice, icon: Monitor },
                { d: 'mobile' as PreviewDevice, icon: Smartphone },
              ].map(({ d, icon: I }) => (
                <button key={d} onClick={() => setPreviewDevice(d)} style={{
                  padding: '5px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
                  background: previewDevice === d ? COLORS.surface : 'transparent',
                  color: previewDevice === d ? COLORS.primary : COLORS.textSecondary,
                  boxShadow: previewDevice === d ? SHADOW.sm : 'none', transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center',
                }}>
                  <I size={14} />
                </button>
              ))}
            </div>
          )}
          <button onClick={() => setShowPreview(!showPreview)} style={{
            padding: '6px 14px', borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`,
            background: showPreview ? COLORS.subtle : COLORS.surface,
            color: showPreview ? COLORS.primary : COLORS.textSecondary,
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <Eye size={12} /> Preview
          </button>
        </div>
      </div>

      {/* Editor + Preview */}
      <div style={{
        display: 'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr',
        gap: 16, minHeight: 500,
      }}>
        {/* Editor */}
        <div style={{
          background: COLORS.surface, borderRadius: RADIUS.lg,
          overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: SHADOW.md,
        }}>
          {/* Toolbar */}
          {mode === 'visual' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 2, padding: '8px 12px',
              borderBottom: `1px solid ${COLORS.border}`, flexWrap: 'wrap',
            }}>
              {[
                { cmd: 'bold', icon: Bold },
                { cmd: 'italic', icon: Italic },
                { cmd: 'underline', icon: Underline },
              ].map(btn => (
                <button key={btn.cmd} onClick={() => execCmd(btn.cmd)} style={toolbarBtnStyle}>
                  <btn.icon size={14} />
                </button>
              ))}
              <div style={{ width: 1, height: 20, background: COLORS.border, margin: '0 4px' }} />
              {[
                { cmd: 'formatBlock', val: 'h2', icon: Heading2 },
                { cmd: 'formatBlock', val: 'h3', icon: Heading3 },
              ].map((btn, i) => (
                <button key={i} onClick={() => execCmd(btn.cmd, btn.val)} style={toolbarBtnStyle}>
                  <btn.icon size={14} />
                </button>
              ))}
              <div style={{ width: 1, height: 20, background: COLORS.border, margin: '0 4px' }} />
              <button onClick={() => execCmd('insertUnorderedList')} style={toolbarBtnStyle}><List size={14} /></button>
              <button onClick={() => execCmd('justifyLeft')} style={toolbarBtnStyle}><AlignLeft size={14} /></button>
              <button onClick={() => execCmd('justifyCenter')} style={toolbarBtnStyle}><AlignCenter size={14} /></button>
              <div style={{ width: 1, height: 20, background: COLORS.border, margin: '0 4px' }} />
              <button onClick={() => { const url = prompt('Link URL:'); if (url) execCmd('createLink', url); }} style={toolbarBtnStyle}>
                <Link2 size={14} />
              </button>
              <button onClick={() => execCmd('insertHorizontalRule')} style={toolbarBtnStyle}><Minus size={14} /></button>
              <div style={{ width: 1, height: 20, background: COLORS.border, margin: '0 4px' }} />
              <input type="color" onChange={e => execCmd('foreColor', e.target.value)} title="Text color"
                style={{ width: 26, height: 26, border: `1px solid ${COLORS.border}`, borderRadius: 4, cursor: 'pointer', padding: 1 }} />
              <input type="color" onChange={e => execCmd('hiliteColor', e.target.value)} title="Highlight"
                style={{ width: 26, height: 26, border: `1px solid ${COLORS.border}`, borderRadius: 4, cursor: 'pointer', padding: 1 }} />
              <div style={{ width: 1, height: 20, background: COLORS.border, margin: '0 4px' }} />
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
                title="Upload image"
                style={{ ...toolbarBtnStyle, opacity: uploadingImage ? 0.5 : 1 }}
              >
                <ImageIcon size={14} />
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {mode === 'visual' ? (
            <VisualEditor initialHtml={bodyHtml} onChange={setBodyHtml} editorRef={editorRef} />
          ) : (
            <textarea
              value={bodyHtml} onChange={e => setBodyHtml(e.target.value)}
              style={{
                flex: 1, padding: 16, border: 'none', resize: 'none',
                fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6,
                color: COLORS.text, outline: 'none', minHeight: 400,
              }}
              spellCheck={false}
            />
          )}
        </div>

        {/* Preview */}
        {showPreview && (
          <div style={{
            background: COLORS.limestone, borderRadius: RADIUS.lg,
            overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: SHADOW.md,
          }}>
            <div style={{
              padding: '8px 16px', background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
              fontSize: 12, fontWeight: 600, color: COLORS.textSecondary,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Eye size={12} />
              {previewDevice === 'mobile' ? 'Mobile Preview (375px)' : 'Desktop Preview'}
            </div>
            <div style={{
              flex: 1, padding: 16, display: 'flex', justifyContent: 'center',
            }}>
              <div style={{
                width: previewDevice === 'mobile' ? 375 : '100%',
                background: COLORS.surface, borderRadius: 4, overflow: 'hidden',
                boxShadow: SHADOW.md, height: '100%',
                transition: 'width 0.3s ease-in-out',
              }}>
                <iframe
                  ref={previewIframeRef}
                  style={{ width: '100%', height: '100%', border: 'none', minHeight: 450 }}
                  title="Email Preview"
                  sandbox=""
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, padding: '14px 20px',
        background: COLORS.surface, borderRadius: RADIUS.lg,
        boxShadow: SHADOW.md,
      }}>
        <input
          value={changeNote} onChange={e => setChangeNote(e.target.value)}
          placeholder="Change note (optional) — e.g., Updated CTA copy"
          style={{
            flex: 1, padding: '8px 12px', borderRadius: RADIUS.sm,
            border: `1.5px solid ${COLORS.border}`, fontSize: 13, color: COLORS.text,
            fontFamily: 'inherit',
          }}
        />
        <button onClick={save} disabled={saving} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '10px 24px', borderRadius: RADIUS.md, border: 'none',
          background: COLORS.primary, color: '#fff', cursor: 'pointer',
          fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1,
        }}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Test email modal */}
      {showTestModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(17,60,43,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: COLORS.surface, borderRadius: RADIUS.xl, padding: 32,
            width: 440, maxWidth: '90vw', boxShadow: SHADOW.lg,
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: COLORS.ink }}>Send Test Email</h3>
            <p style={{ color: COLORS.textSecondary, fontSize: 14, margin: '0 0 20px', lineHeight: 1.5 }}>
              Merge fields will use example values. Subject will be prefixed with [TEST].
            </p>
            <label style={formLabelStyle}>Recipient</label>
            <input
              value={testEmail} onChange={e => setTestEmail(e.target.value)}
              placeholder="recipient@example.com" type="email"
              style={{
                width: '100%', padding: '10px 14px', borderRadius: RADIUS.sm,
                border: `1.5px solid ${COLORS.border}`, fontSize: 14, marginBottom: 20,
                boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowTestModal(false)} style={{
                padding: '10px 20px', borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`,
                background: COLORS.surface, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.text,
              }}>Cancel</button>
              <button onClick={sendTest} disabled={testSending || !testEmail} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 20px', borderRadius: RADIUS.md, border: 'none',
                background: COLORS.primary, color: '#fff', cursor: 'pointer',
                fontSize: 14, fontWeight: 600, opacity: (testSending || !testEmail) ? 0.7 : 1,
              }}>
                <Send size={14} /> {testSending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Visual Editor ───────────────────────────────────────────────

const VisualEditor: React.FC<{
  initialHtml: string;
  onChange: (html: string) => void;
  editorRef: React.RefObject<HTMLDivElement>;
}> = ({ initialHtml, onChange, editorRef }) => {
  const mountedHtml = useRef('');

  useEffect(() => {
    // Only reinitialize on genuine content switch (e.g. selecting a different email),
    // not on every parent re-render from our own edits.
    if (editorRef.current && initialHtml !== mountedHtml.current) {
      editorRef.current.innerHTML = DOMPurify.sanitize(initialHtml);
      mountedHtml.current = initialHtml;
    }
  }, [initialHtml, editorRef]);

  return (
    <div
      ref={editorRef}
      contentEditable
      onBlur={() => {
        if (editorRef.current) {
          const html = DOMPurify.sanitize(editorRef.current.innerHTML);
          mountedHtml.current = html;
          onChange(html);
        }
      }}
      style={{
        flex: 1, padding: 20, outline: 'none', fontSize: 14,
        lineHeight: 1.6, color: COLORS.text, overflow: 'auto',
        minHeight: 400,
      }}
      suppressContentEditableWarning
    />
  );
};

// ── Shared styles ───────────────────────────────────────────────

const toolbarBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32, borderRadius: RADIUS.sm,
  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
  cursor: 'pointer', color: COLORS.text, transition: 'all 0.1s',
};

const headerBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: RADIUS.md,
  border: `1.5px solid ${COLORS.border}`, background: COLORS.surface,
  cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.text,
  transition: 'all 0.15s ease-in-out',
};

const formLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textSecondary,
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
};

// ── Unsubscribe Section ─────────────────────────────────────────

interface PreferenceCategory {
  id?: string;
  slug: string;
  name: string;
  description: string | null;
  is_transactional: boolean;
  sort_order: number;
  is_active: boolean;
}

interface UnsubscribeStats {
  total_unsubscribes: number;
  unsubscribes_last_30_days: number;
  total_opt_outs: number;
  users_with_preferences: number;
  category_stats: Array<{ category: string; opted_in: number; opted_out: number }>;
  recent_activity: Array<{
    action: string;
    category: string | null;
    source: string | null;
    timestamp: string | null;
  }>;
}

// Human-readable labels + help text for the unsubscribe landing-page strings
// that live in `email_unsubscribe_settings`. Order matters — it's the render
// order in the editor.
const SETTINGS_FIELDS: Array<{
  key: string;
  label: string;
  help: string;
  multiline?: boolean;
}> = [
  { key: 'page_heading', label: 'Page Heading', help: 'Hero bubble text at the top of coachcedar.com/unsubscribe.' },
  { key: 'page_subheading', label: 'Page Subheading', help: 'One-line intro under the heading.', multiline: true },
  { key: 'unsubscribe_all_heading', label: '“Unsubscribe All” Heading', help: 'Heading of the opt-out-everything section.' },
  { key: 'unsubscribe_all_description', label: '“Unsubscribe All” Description', help: 'Explains what clicking the button does.', multiline: true },
  { key: 'confirmation_heading', label: 'Save Confirmation Heading', help: 'Shown after the user saves their preferences.' },
  { key: 'confirmation_message', label: 'Save Confirmation Message', help: 'Body text after preferences are saved.', multiline: true },
  { key: 'unsubscribe_all_confirmation', label: '“Unsubscribe All” Confirmation', help: 'Body text after unsubscribing from everything.', multiline: true },
  { key: 'resubscribe_message', label: 'Re-subscribe Note', help: 'Shown under the success message to remind users they can re-enable.', multiline: true },
  { key: 'footer_note', label: 'Footer Note', help: 'Small compliance note at the bottom of the page.', multiline: true },
];

const UnsubscribeSection: React.FC<{ onToast: (msg: string, type?: 'success' | 'error' | 'info') => void }> = ({ onToast }) => {
  const [stats, setStats] = useState<UnsubscribeStats | null>(null);
  const [categories, setCategories] = useState<PreferenceCategory[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [settingsDraft, setSettingsDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingSlug, setTogglingSlug] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [statsRes, catsRes, settingsRes] = await Promise.all([
        AdminEmailService.getUnsubscribeStats(),
        AdminEmailService.listPreferenceCategories(),
        AdminEmailService.getUnsubscribeSettings(),
      ]);
      const { success: _s, ...statsData } = statsRes;
      setStats(statsData as UnsubscribeStats);
      setCategories((catsRes.categories as PreferenceCategory[]) || []);
      const s = (settingsRes.settings as Record<string, string>) || {};
      setSettings(s);
      setSettingsDraft(s);
    } catch (e) {
      console.error('Unsubscribe load failed:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleActive = async (cat: PreferenceCategory) => {
    if (cat.is_transactional) {
      onToast('Transactional categories cannot be deactivated.', 'info');
      return;
    }
    setTogglingSlug(cat.slug);
    try {
      await AdminEmailService.updatePreferenceCategory(cat.slug, { is_active: !cat.is_active });
      setCategories(prev => prev.map(c => c.slug === cat.slug ? { ...c, is_active: !c.is_active } : c));
      onToast(`“${cat.name}” ${!cat.is_active ? 'activated' : 'deactivated'}.`, 'success');
    } catch (e: any) {
      onToast(e?.message || 'Failed to update category.', 'error');
    } finally {
      setTogglingSlug(null);
    }
  };

  const dirtyKeys = Object.keys(settingsDraft).filter(k => settingsDraft[k] !== (settings[k] ?? ''));
  const hasDirty = dirtyKeys.length > 0;

  const handleSaveSettings = async () => {
    if (!hasDirty) return;
    setSavingSettings(true);
    try {
      const payload: Record<string, string> = {};
      for (const k of dirtyKeys) payload[k] = settingsDraft[k];
      const res = await AdminEmailService.updateUnsubscribeSettings(payload);
      const next = (res.settings as Record<string, string>) || { ...settings, ...payload };
      setSettings(next);
      setSettingsDraft(next);
      onToast('Unsubscribe page content saved.', 'success');
    } catch (e: any) {
      onToast(e?.message || 'Failed to save settings.', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleResetDraft = () => setSettingsDraft(settings);

  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <AlertCircle size={24} color={COLORS.error} style={{ marginBottom: 8 }} />
      <div style={{ color: COLORS.error, fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Failed to load unsubscribe data</div>
      <div style={{ color: COLORS.textSecondary, fontSize: 13, marginBottom: 12 }}>Check your connection and try again.</div>
      <button
        onClick={load}
        style={{ ...headerBtnStyle, margin: '0 auto' }}
      >
        Retry
      </button>
    </div>
  );

  const statCards = [
    { label: 'Total Unsubscribe-All', value: stats?.total_unsubscribes ?? 0, icon: MailX, color: COLORS.error },
    { label: 'Last 30 Days', value: stats?.unsubscribes_last_30_days ?? 0, icon: Send, color: COLORS.warning },
    { label: 'Category Opt-Outs', value: stats?.total_opt_outs ?? 0, icon: ToggleLeft, color: COLORS.info },
    { label: 'Users With Prefs', value: stats?.users_with_preferences ?? 0, icon: Users, color: COLORS.primary },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: COLORS.ink, margin: 0, fontFamily: "'League Spartan', sans-serif" }}>
          Unsubscribe & Preferences
        </h2>
        <p style={{ color: COLORS.textSecondary, marginTop: 4, fontSize: 14 }}>
          Manage preference categories, the unsubscribe landing-page content at{' '}
          <code style={{ fontSize: 13, background: COLORS.limestone, padding: '1px 6px', borderRadius: 4 }}>coachcedar.com/unsubscribe</code>,
          and opt-out analytics.
        </p>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} style={{
              background: COLORS.surface, borderRadius: RADIUS.lg, padding: '20px 24px',
              boxShadow: SHADOW.md,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: RADIUS.md,
                  background: `${stat.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={18} color={stat.color} />
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.ink }}>{stat.value}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.textSecondary, marginTop: 2 }}>{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Categories */}
      <div style={{
        background: COLORS.surface, borderRadius: RADIUS.lg, boxShadow: SHADOW.md,
        marginBottom: 24, overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '16px 20px', borderBottom: `1px solid ${COLORS.borderLight}`,
        }}>
          <Tag size={16} color={COLORS.primary} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.ink, margin: 0 }}>Preference Categories</h3>
          <span style={{ color: COLORS.textTertiary, fontSize: 12, marginLeft: 'auto' }}>
            {categories.length} total · {categories.filter(c => c.is_active).length} active
          </span>
        </div>
        <div>
          {categories.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: COLORS.textSecondary, fontSize: 13 }}>
              No categories defined yet.
            </div>
          ) : (
            categories
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((cat, idx, arr) => {
                const perCat = stats?.category_stats?.find(cs => cs.category === cat.slug);
                return (
                  <div
                    key={cat.slug}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      padding: '14px 20px',
                      borderBottom: idx < arr.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
                      opacity: cat.is_active ? 1 : 0.55,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.ink }}>{cat.name}</span>
                        <code style={{ fontSize: 11, background: COLORS.limestone, padding: '1px 6px', borderRadius: 4, color: COLORS.textSecondary }}>
                          {cat.slug}
                        </code>
                        {cat.is_transactional && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                            padding: '2px 8px', borderRadius: RADIUS.pill,
                            background: COLORS.infoBg, color: COLORS.info,
                          }}>Transactional</span>
                        )}
                        {!cat.is_active && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                            padding: '2px 8px', borderRadius: RADIUS.pill,
                            background: COLORS.errorBg, color: COLORS.error,
                          }}>Inactive</span>
                        )}
                      </div>
                      {cat.description && (
                        <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.4 }}>
                          {cat.description}
                        </div>
                      )}
                      {perCat && (
                        <div style={{ fontSize: 12, color: COLORS.textTertiary, marginTop: 4 }}>
                          {perCat.opted_in} opted in · {perCat.opted_out} opted out
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleToggleActive(cat)}
                      disabled={cat.is_transactional || togglingSlug === cat.slug}
                      title={cat.is_transactional ? 'Transactional categories cannot be deactivated' : (cat.is_active ? 'Deactivate' : 'Activate')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '6px 10px', borderRadius: RADIUS.md,
                        border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                        cursor: cat.is_transactional ? 'not-allowed' : 'pointer',
                        fontSize: 12, fontWeight: 600,
                        color: cat.is_transactional ? COLORS.textTertiary : (cat.is_active ? COLORS.success : COLORS.textSecondary),
                        opacity: cat.is_transactional ? 0.6 : 1,
                      }}
                    >
                      {cat.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      {cat.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                );
              })
          )}
        </div>
      </div>

      {/* Settings editor */}
      <div style={{
        background: COLORS.surface, borderRadius: RADIUS.lg, boxShadow: SHADOW.md,
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '16px 20px', borderBottom: `1px solid ${COLORS.borderLight}`,
        }}>
          <Edit3 size={16} color={COLORS.primary} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.ink, margin: 0 }}>Unsubscribe Page Content</h3>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              onClick={handleResetDraft}
              disabled={!hasDirty || savingSettings}
              style={{
                ...headerBtnStyle,
                opacity: !hasDirty || savingSettings ? 0.5 : 1,
                cursor: !hasDirty || savingSettings ? 'not-allowed' : 'pointer',
              }}
            >
              Reset
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={!hasDirty || savingSettings}
              style={{
                ...headerBtnStyle,
                background: hasDirty ? COLORS.primary : COLORS.surface,
                color: hasDirty ? '#FFF' : COLORS.text,
                borderColor: hasDirty ? COLORS.primary : COLORS.border,
                opacity: !hasDirty || savingSettings ? 0.7 : 1,
                cursor: !hasDirty || savingSettings ? 'not-allowed' : 'pointer',
              }}
            >
              <Save size={14} />
              {savingSettings ? 'Saving...' : hasDirty ? `Save (${dirtyKeys.length})` : 'Saved'}
            </button>
          </div>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {SETTINGS_FIELDS.map(field => {
            const value = settingsDraft[field.key] ?? '';
            const isDirty = value !== (settings[field.key] ?? '');
            const commonStyle: React.CSSProperties = {
              width: '100%', padding: '10px 12px', borderRadius: RADIUS.md,
              border: `1px solid ${isDirty ? COLORS.primary : COLORS.border}`,
              background: COLORS.surface, color: COLORS.text,
              fontSize: 14, fontFamily: 'inherit', lineHeight: 1.5,
              outline: 'none', transition: 'border-color 0.15s',
              boxSizing: 'border-box',
            };
            return (
              <div key={field.key}>
                <label style={{ ...formLabelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {field.label}
                  {isDirty && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                      padding: '1px 6px', borderRadius: RADIUS.pill,
                      background: `${COLORS.primary}18`, color: COLORS.primary,
                    }}>UNSAVED</span>
                  )}
                </label>
                {field.multiline ? (
                  <textarea
                    value={value}
                    onChange={e => setSettingsDraft(prev => ({ ...prev, [field.key]: e.target.value }))}
                    rows={3}
                    style={{ ...commonStyle, resize: 'vertical', minHeight: 72 }}
                  />
                ) : (
                  <input
                    type="text"
                    value={value}
                    onChange={e => setSettingsDraft(prev => ({ ...prev, [field.key]: e.target.value }))}
                    style={commonStyle}
                  />
                )}
                <div style={{ fontSize: 12, color: COLORS.textTertiary, marginTop: 4 }}>{field.help}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent activity */}
      {stats?.recent_activity && stats.recent_activity.length > 0 && (
        <div style={{
          marginTop: 24,
          background: COLORS.surface, borderRadius: RADIUS.lg, boxShadow: SHADOW.md,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '16px 20px', borderBottom: `1px solid ${COLORS.borderLight}`,
          }}>
            <History size={16} color={COLORS.primary} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.ink, margin: 0 }}>Recent Activity</h3>
          </div>
          <div>
            {stats.recent_activity.map((a, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 20px',
                  borderBottom: i < stats.recent_activity.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
                  fontSize: 13, color: COLORS.text,
                }}
              >
                <span style={{
                  fontWeight: 600,
                  color: a.action === 'unsubscribe_all' ? COLORS.error : a.action === 'opt_out' ? COLORS.warning : COLORS.success,
                  minWidth: 120,
                }}>
                  {a.action.replace(/_/g, ' ')}
                </span>
                <span style={{ color: COLORS.textSecondary, minWidth: 140 }}>
                  {a.category || '—'}
                </span>
                <span style={{ color: COLORS.textTertiary, fontSize: 12 }}>
                  {a.source || 'unknown'}
                </span>
                <span style={{ marginLeft: 'auto', color: COLORS.textTertiary, fontSize: 12 }}>
                  {a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
